import { useContext, useRef, useState } from "react"
import { Div, Col, Row, Text, Image } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faImage, faXmark } from "@fortawesome/free-solid-svg-icons"
import { toast } from "react-toastify"
import { isMobile, isTablet } from "react-device-detect"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import Container from "../atoms/Container"
import Paragraph from "../atoms/Paragraph"
import Chip from "../atoms/Chip"
import TextArea from "../inputs/TextArea"
import { theme } from "../settings/config"
import { APP_VERSION, BUILD_COMMIT } from "../settings/version"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { lessonsContext } from "../providers/lessons.provider"
import { useTheme } from "../providers/theme.provider"
import { addFeedback } from "../utils/firestore.utils"
import { isOffline } from "../utils/network.utils"
import { roleOf } from "../utils/school.utils"
import {
	FEEDBACK_MAX_TEXT,
	FEEDBACK_SEVERITIES,
	buildFeedback,
	buildFeedbackContext,
	canSendFeedback,
	screenshotToDataURI,
	visitedPages,
} from "../utils/feedback.utils"

// Corto apposta: da telefono, a bordo campo, si compila in meno di trenta
// secondi. Chi è, dove si trovava e con che app lo mettiamo noi.
export function FeedbackModal({ closeModal }) {
	const { coach } = useContext(profileContext)
	const { schools, selectedSchool } = useContext(schoolContext)
	const { selectedDate, lessonsView } = useContext(lessonsContext)
	const { mode: themeMode, theme: currentTheme } = useTheme()

	const [text, setText] = useState("")
	const [severity, setSeverity] = useState(null)
	const [screenshot, setScreenshot] = useState(null)
	const [isPreparingScreenshot, setPreparingScreenshot] = useState(false)
	const [isSending, setSending] = useState(false)
	const fileInput = useRef(null)

	const handleScreenshot = async event => {
		const file = event.target.files?.[0]
		// si può riscegliere la stessa immagine dopo averla tolta
		event.target.value = ""
		if (!file) return

		setPreparingScreenshot(true)
		try {
			setScreenshot(await screenshotToDataURI(file))
		} catch (err) {
			toast.error(err.message || "Non riesco ad aprire questa immagine", {
				position: "top-center",
			})
		} finally {
			setPreparingScreenshot(false)
		}
	}

	const buildContext = () => {
		const { page, previousPage } = visitedPages()
		return buildFeedbackContext({
			coachID: coach?.coachID,
			schoolID: selectedSchool,
			role: selectedSchool ? roleOf(coach, schools?.[selectedSchool], selectedSchool) : null,
			page,
			previousPage,
			selectedDate,
			lessonsView,
			appVersion: APP_VERSION,
			buildCommit: BUILD_COMMIT,
			device: isTablet ? "tablet" : isMobile ? "phone" : "computer",
			theme: currentTheme,
			themeMode,
			online: !isOffline(),
			userAgent: navigator.userAgent,
			screen: `${window.innerWidth}x${window.innerHeight}`,
		})
	}

	const handleSubmit = async () => {
		setSending(true)
		try {
			const delivered = await addFeedback(
				buildFeedback({ text, severity, context: buildContext(), screenshot }),
			)
			toast.success(
				delivered
					? "Segnalazione inviata, grazie"
					: "Segnalazione salvata, grazie: parte appena torna la rete",
				{ position: "top-center" },
			)
			closeModal()
		} catch (err) {
			console.error("Errore invio segnalazione:", err)
			toast.error("Non sono riuscito a inviare la segnalazione", { position: "top-center" })
			setSending(false)
		}
	}

	const isBusy = isSending || isPreparingScreenshot

	return (
		<>
			<Container>
				<Div m={{ b: "0.5rem" }} textWeight="bold" textSize="title">
					Segnala un problema
				</Div>
			</Container>
			<Div maxH="60vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<Container>
					<Paragraph title="Cosa è successo, o cosa ti è mancato?">
						<TextArea
							value={text}
							handleChange={e => setText(e.target.value.slice(0, FEEDBACK_MAX_TEXT))}
						/>
					</Paragraph>
					{text.length > FEEDBACK_MAX_TEXT - 200 && (
						<Text
							textSize="caption"
							textColor={theme.colors.text_light}
							m={{ t: "-0.75rem", b: "0.75rem" }}>
							{text.length}/{FEEDBACK_MAX_TEXT}
						</Text>
					)}

					<Paragraph title="Quanto ti blocca?" m={{ b: "0.5rem" }}>
						<Div d="flex" flexWrap="wrap" style={{ gap: "0.5rem" }}>
							{FEEDBACK_SEVERITIES.map(option => (
								<Chip
									key={option.value}
									text={option.text}
									isSelected={severity === option.value}
									onClick={() => setSeverity(option.value)}
									disabled={isSending}
								/>
							))}
						</Div>
					</Paragraph>

					<Div m={{ t: "1rem" }}>
						<input
							ref={fileInput}
							type="file"
							accept="image/*"
							style={{ display: "none" }}
							onChange={handleScreenshot}
						/>
						{screenshot ? (
							<Div d="flex" align="center" style={{ gap: "0.75rem" }}>
								<Image
									src={screenshot}
									alt="Screenshot allegato"
									h="5rem"
									w="auto"
									rounded="8px"
								/>
								<Div
									d="flex"
									align="center"
									cursor="pointer"
									style={{ gap: "0.4rem" }}
									onClick={() => !isSending && setScreenshot(null)}>
									<FontAwesomeIcon icon={faXmark} color={theme.colors.text_light} />
									<Text textColor={theme.colors.text_light}>Togli</Text>
								</Div>
							</Div>
						) : (
							<Div
								d="flex"
								align="center"
								cursor="pointer"
								style={{ gap: "0.5rem" }}
								onClick={() => !isBusy && fileInput.current?.click()}>
								<FontAwesomeIcon icon={faImage} color={theme.colors.text_light} />
								<Text textColor={theme.colors.gunmetal}>
									{isPreparingScreenshot
										? "Preparo l'immagine..."
										: "Aggiungi uno screenshot (facoltativo)"}
								</Text>
							</Div>
						)}
					</Div>
				</Container>
			</Div>
			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							text={isSending ? "Invio..." : "Indietro"}
							textSize="subheader"
							disabled={isSending}
							action={closeModal}
						/>
					</Col>
					{!isSending && (
						<Col p="0">
							<PrimaryButton
								m="auto"
								bg={theme.colors.primary}
								textColor="white"
								textSize="subheader"
								text="Invia"
								disabled={isBusy || !canSendFeedback({ text, severity })}
								action={handleSubmit}
							/>
						</Col>
					)}
				</Row>
			</Div>
		</>
	)
}

export default FeedbackModal
